/**
 * One editor per draft (ADR-0006). Whoever opens a draft holds the lock;
 * another reporter (or the same reporter on another device) must explicitly
 * take it over. Locks do not expire on their own, because a reporter may be
 * offline for days.
 */

export interface DraftLock {
  surveyId: string;
  holderUserId: string;
  holderDeviceId: string;
  acquiredAt: string;
}

export type LockDecision =
  | { editable: true }
  | { editable: false; reason: 'HELD_BY_OTHER_USER' | 'HELD_ON_OTHER_DEVICE'; holder: DraftLock };

export function canEditDraft(lock: DraftLock | null, userId: string, deviceId: string): LockDecision {
  if (!lock) return { editable: true };
  if (lock.holderUserId !== userId) return { editable: false, reason: 'HELD_BY_OTHER_USER', holder: lock };
  if (lock.holderDeviceId !== deviceId) return { editable: false, reason: 'HELD_ON_OTHER_DEVICE', holder: lock };
  return { editable: true };
}

/** Taking over another user's draft needs survey.takeover; your own on another device does not. */
export function takeoverNeedsPermission(lock: DraftLock, userId: string): boolean {
  return lock.holderUserId !== userId;
}

/**
 * Offline session limit (ADR-0021): after `offlineReauthDays` without
 * reaching the server, the app must sign in online again. Drafts are kept
 * but locked until then.
 */
export function offlineSessionValid(lastServerContactMs: number, nowMs: number, offlineReauthDays: number): boolean {
  return nowMs - lastServerContactMs < offlineReauthDays * 86_400_000;
}

/** Warn when unsynced work has been waiting longer than this (ADR-0006). */
export const UNSYNCED_WARNING_MS = 24 * 3_600_000;
